import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Icon,
  Paper,
  Chip,
  IconButton,
  Grid,
  InputAdornment,
  alpha,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DepartmentContact } from "../../../services/facilities.service";

interface Department {
  _id?: string;
  name: string;
  address?: string;
  contacts?: DepartmentContact[];
}

interface DepartmentFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: Department) => void;
  department: Department | null;
  loading?: boolean;
}

const initialFormState: Department = {
  name: "",
  address: "",
  contacts: [],
};

export const DepartmentFormDialog: React.FC<DepartmentFormDialogProps> = ({
  open,
  onClose,
  onSave,
  department,
  loading = false,
}) => {
  const [formData, setFormData] = useState<Department>(initialFormState);
  const [errors, setErrors] = useState<{
    name?: string;
    address?: string;
    contacts?: string;
  }>({});

  useEffect(() => {
    if (department) {
      setFormData({
        ...department,
        address: department.address || "",
        contacts: department.contacts || [],
      });
    } else {
      setFormData(initialFormState);
    }
    setErrors({});
  }, [department, open]);

  const handleChange =
    (field: keyof Department) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: e.target.value,
      }));
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    };

  const handleContactChange =
    (index: number, field: keyof DepartmentContact) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newContacts = [...(formData.contacts || [])];
      newContacts[index] = {
        ...newContacts[index],
        [field]: e.target.value,
      };
      setFormData((prev) => ({
        ...prev,
        contacts: newContacts,
      }));
    };

  const addContact = () => {
    const currentContacts = formData.contacts || [];
    setFormData((prev) => ({
      ...prev,
      contacts: [
        ...currentContacts,
        { name: "", email: "", phone: "", whatsapp: "" },
      ],
    }));
    setErrors((prev) => ({
      ...prev,
      contacts: "",
    }));
  };

  const removeContact = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      contacts: (prev.contacts || []).filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          py: 2,
          background: "#0891B2",
          color: "white",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1.5}>
            <Icon sx={{ fontSize: 20 }}>domain</Icon>
            <Typography variant="subtitle1" fontWeight={600}>
              {department ? "Edit Department" : "Add New Department"}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: "white",
              "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Department Name"
                value={formData.name}
                onChange={handleChange("name")}
                error={!!errors.name}
                helperText={errors.name || "Enter the name of the department"}
                placeholder="e.g., Human Resources"
                required
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "background.paper",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address"
                value={formData.address}
                onChange={handleChange("address")}
                error={!!errors.address}
                helperText={errors.address}
                placeholder="e.g., Building A, 3rd Floor, West Wing"
                multiline
                rows={2}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "background.paper",
                  },
                }}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 4 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Icon sx={{ color: "text.secondary" }}>contacts</Icon>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Contact Information
                </Typography>
                <Chip
                  label={`${formData.contacts?.length || 0} contact${
                    (formData.contacts?.length || 0) !== 1 ? "s" : ""
                  }`}
                  size="small"
                  sx={{
                    bgcolor: (theme) =>
                      alpha(
                        "#0891B2",
                        (formData.contacts?.length || 0) > 0 ? 0.1 : 0.05
                      ),
                    color: "#0891B2",
                    fontWeight: 500,
                  }}
                />
              </Box>
              <Button
                startIcon={<Icon>add</Icon>}
                onClick={addContact}
                variant="outlined"
                sx={{
                  borderColor: "#0891B2",
                  color: "#0891B2",
                  "&:hover": {
                    borderColor: "#0E7490",
                    bgcolor: alpha("#0891B2", 0.04),
                  },
                }}
              >
                Add Contact
              </Button>
            </Box>

            {errors.contacts && (
              <Typography
                color="error"
                variant="caption"
                sx={{ mb: 2, display: "block" }}
              >
                {errors.contacts}
              </Typography>
            )}

            {!formData.contacts?.length ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 1,
                  bgcolor: alpha("#0891B2", 0.02),
                  border: "1px dashed",
                  borderColor: alpha("#0891B2", 0.2),
                  textAlign: "center",
                }}
              >
                <Typography color="textSecondary">
                  No contacts added yet. Click the button above to add contacts.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {formData.contacts.map((contact, index) => (
                  <Paper
                    key={index}
                    variant="outlined"
                    sx={{
                      p: 3,
                      borderRadius: 1,
                      bgcolor: "background.paper",
                      borderColor: alpha("#0891B2", 0.1),
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Icon sx={{ fontSize: 18, color: "text.secondary" }}>
                          person
                        </Icon>
                        Contact #{index + 1}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => removeContact(index)}
                        sx={{
                          color: "#DC2626",
                          "&:hover": {
                            bgcolor: alpha("#DC2626", 0.04),
                          },
                        }}
                      >
                        <Icon sx={{ fontSize: 18 }}>delete</Icon>
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Contact Name"
                          value={contact.name}
                          onChange={handleContactChange(index, "name")}
                          placeholder="e.g., John Doe"
                          required
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "background.paper",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Email"
                          value={contact.email}
                          onChange={handleContactChange(index, "email")}
                          placeholder="e.g., john@example.com"
                          type="email"
                          required
                          size="small"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "background.paper",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Phone"
                          value={contact.phone}
                          onChange={handleContactChange(index, "phone")}
                          placeholder="e.g., +1234567890"
                          required
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon
                                  sx={{ fontSize: 18, color: "text.secondary" }}
                                >
                                  phone
                                </Icon>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "background.paper",
                            },
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="WhatsApp"
                          value={contact.whatsapp}
                          onChange={handleContactChange(index, "whatsapp")}
                          placeholder="e.g., +1234567890"
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Icon
                                  sx={{ fontSize: 18, color: "text.secondary" }}
                                >
                                  whatsapp
                                </Icon>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: "background.paper",
                            },
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderColor: alpha("#0891B2", 0.5),
            color: "#0891B2",
            "&:hover": {
              borderColor: "#0891B2",
              bgcolor: alpha("#0891B2", 0.04),
            },
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={loading}
          loadingPosition="start"
          startIcon={<Icon>save</Icon>}
          sx={{
            bgcolor: "#0891B2",
            "&:hover": {
              bgcolor: "#0E7490",
            },
          }}
        >
          Save Department
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
